import { title } from "process";
import ItemDescriptionJson, { defaultItemDescriptionJson, validateItemDescriptionJson, validateItemDescriptionJsonAdd } from "./ItemDescriptionJson";
import RentalConditionJson, { validateRentalConditionJson, validateRentalConditionJsonAdd } from "./RentalConditionJson";
import { Validation, ValidationErrorType, ValidationResult } from "./Validation";
import CategoryTypeJson, { validateCategoryTypeJson } from './CategoryTypeJson';

/**
 * Beschreibt einen Mietgegenstand.
 */
export default interface RentalItemJson {
    ID: number | null;

    /**
     * Name des Mietgegenstands.
     */
    name: string;

    /**
     * Kategorie des Mietgegenstands.
     */
    category: string;

    /**
     * Beschreibung des Mietgegenstands. Kann null sein, da die Beschreibung nur für Einzelansicht benötigt wird und in Listenansichten nicht übertragen wird.
     */
    description: ItemDescriptionJson | null;

    /**
     * Gibt an, ob dieses Produkt kontaktlos zurückgegeben werden kann/darf.
     */
    contactlessReturn: boolean;

    /**
     * Gibt an, ob für dieses Produkt der Führerschein hochgeladen werden muss.
     */
    requestDriversLicense: boolean;

    /**
    * Gibt Kategorien an, auf die dieses Produkt beschränkt ist. Kann null sein, wenn keine Beschränkung vorliegt.
    */
    restrictToTypes: CategoryTypeJson[] | null;

    /**
     * Notizenfeld für die Verwaltung.
     */
    remarks: string;

    /**
     * Anzahl der verfügbaren Gegenstände.
     */
    amount: number;

    /**
     * Link mit weiteren Informationen zum Verleih der in der Bestätigungsmail mitgesendet wird.
     */
    confirmationMailLink: string;

    /**
     * Liste aller möglichen Verleihbediungen, zu denen dieser Gegenstand gemietet werden kann.
     */
    rentalConditions: RentalConditionJson[];

    /**
     * Liste aller möglichen Zusatzoptionen, die für diesen Gegenstand gemietet werden können.
     */
    additionalRentalConditions: RentalConditionJson[];
}

export type RentalItemInfoJson = Omit<RentalItemJson, "remarks">;

export function defaultRentalItemJson(): RentalItemJson {
    return {
        ID: null,
        name: "",
        category: "",
        description: defaultItemDescriptionJson(),
        contactlessReturn: false,
        requestDriversLicense: false,
        restrictToTypes: null,
        remarks: "",
        amount: 1,
        confirmationMailLink: "",
        rentalConditions: [],
        additionalRentalConditions: [],
    };
}

export const RentalItemAllowedTags: string[] = ["p", "br", "b", "ul", "li", "u"];

export function validateRentalItemJson(json: RentalItemJson): ValidationResult<RentalItemJson> {
    return new Validation(json)
        .validateInteger("ID", 0, null, false)
        .validateNonEmptyString("name", 100)
        .validateNonEmptyString("category", 100)
        .validateUsingFunction("description", validateItemDescriptionJson, true)
        .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
        .validateString("remarks", 1000)
        .validateInteger("amount", 0, 10000, false)
        .validateString("confirmationMailLink", 200)
        .validateAllUsingFunction("rentalConditions", validateRentalConditionJson, 1, false)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJson, 0, false)
        .finish();
}
export function validateRentalItemJsonBooking(json: RentalItemJson): ValidationResult<RentalItemJson> {
    return new Validation(json)
        .validateInteger("ID", 0, null, false)
        .validateNonEmptyString("name", 100)
        .validateNonEmptyString("category", 100)
        .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
        .validateString("remarks", 1000)
        .validateInteger("amount", 0, 10000, false)
        .validateString("confirmationMailLink", 200)
        .validateAllUsingFunction("rentalConditions", validateRentalConditionJson, 1, false)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJson, 0, false)
        .finish();
}
export function validateRentalItemJsonAdd(json: RentalItemJson): ValidationResult<RentalItemJson> {
    return new Validation(json)
        .validateEquals("ID", null)
        .validateNonEmptyString("name", 100)
        .validateNonEmptyString("category", 100)
        .validateUsingFunction("description", validateItemDescriptionJsonAdd, true)
        .validateAllUsingFunction("restrictToTypes", validateCategoryTypeJson, 0, true)
        .validateString("remarks", 1000)
        .validateInteger("amount", 0, 10000, false)
        .validateString("confirmationMailLink", 200)
        .validateAllUsingFunction("rentalConditions", validateRentalConditionJsonAdd, 1, false)
        .validateAllUsingFunction("additionalRentalConditions", validateRentalConditionJsonAdd, 0, false)
        .finish();
}

function createValidateRentalConditionJsonTitleUnique(titles: string[]): (c: RentalConditionJson) => ValidationResult<RentalConditionJson> {
    return function (c: RentalConditionJson) {
        let validation = validateRentalConditionJson(c);
        if (!validation.errors["title"] && titles.filter(t => t == c.title).length > 1) {
            validation.errors["title"] = ValidationErrorType.duplicate;
            return new ValidationResult(validation.errors);
        }
        return validation;
    };
}